import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { useBlockGroupContext } from "components/form/Blocks";
import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useSectionFourImagesVerticalApi from "hooks/api/private/blocks/useSectionFourImagesVerticalApi";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import {
    SectionFourImagesVerticalBody,
    SectionFourImagesVerticalDetails,
} from "types/api/blocks/sectionFourImagesVertical";
import { DesktopMobileForm } from "types/api/commons";
import { DesktopOrMobile } from "types/enums";

import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import { getExtraErrorsDescription, notEmptyMedia } from "utils/validators";

export type TwoImageTextureForm = {
    id: string | undefined;
    left_img_id: string | undefined;
    left_img: DesktopMobileForm;
    right_img_id: string | undefined;
    right_img: DesktopMobileForm;
    center_left_img_id: string | undefined;
    center_left_img: DesktopMobileForm;
    center_right_img_id: string | undefined;
    center_right_img: DesktopMobileForm;
};

type SectionFourImagesVerticalType = {
    setCenterLeftImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    setCenterRightImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    setRightImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    setLeftImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    buttonDelete: ButtonDeleteInterface;
    formHook: FormHookType<TwoImageTextureForm>;
};

function useSectionFourImagesVertical(): SectionFourImagesVerticalType {
    const {
        saveSectionFourImagesVertical,
        getOneSectionFourImagesVertical,
        deleteSectionFourImagesVertical,
    } = useSectionFourImagesVerticalApi();

    const { blockGroupCompositionId, blockGroupId, blockId, graphicalOrder } =
        useBlockGroupContext();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                id: {
                    type: "simple",
                    validators: [],
                },
                left_img_id: {
                    type: "simple",
                    validators: [],
                },
                right_img_id: {
                    type: "simple",
                    validators: [],
                },
                center_left_id: {
                    type: "simple",
                    validators: [],
                },
                center_right_id: {
                    type: "simple",
                    validators: [],
                },
                left_img: notEmptyMedia,
                right_img: notEmptyMedia,
                center_left_img: notEmptyMedia,
                center_right_img: notEmptyMedia,
            },
        };
    }, []);

    const initialValues: TwoImageTextureForm = React.useMemo(() => {
        return {
            id: undefined,
            left_img_id: undefined,
            left_img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            right_img_id: undefined,
            right_img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            center_left_img_id: undefined,
            center_left_img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            center_right_img_id: undefined,
            center_right_img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
        };
    }, []);

    const formHook = useForm<TwoImageTextureForm>(schema, initialValues, getExtraErrorsDescription);

    const { showAllError, setForm, isValid, form } = formHook;

    const { setDesktopMobileImage: setLeftImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "left_img",
    });

    const { setDesktopMobileImage: setRightImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "right_img",
    });

    const { setDesktopMobileImage: setCenterLeftImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "center_left_img",
    });

    const { setDesktopMobileImage: setCenterRightImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "center_right_img",
    });

    const [twoImagesTextureDetails, setTwoImageTextureDetails] = React.useState<
        SectionFourImagesVerticalDetails | undefined
    >(undefined);

    React.useEffect(() => {
        const [apiCall, abortController] = getOneSectionFourImagesVertical(
            { id: blockGroupCompositionId },
            { rejectNotFound: true }
        );

        apiCall.then(setTwoImageTextureDetails).catch((error) => console.log(error));

        return () => abortController.abort();
    }, [setTwoImageTextureDetails, getOneSectionFourImagesVertical, blockGroupCompositionId]);

    React.useEffect(() => {
        if (twoImagesTextureDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = twoImagesTextureDetails.id;
                newForm.left_img_id = twoImagesTextureDetails.left_img_id;
                newForm.right_img_id = twoImagesTextureDetails.right_img_id;
                newForm.center_left_img_id = twoImagesTextureDetails.center_left_img_id;
                newForm.center_right_img_id = twoImagesTextureDetails.center_right_img_id;
                newForm.left_img = {
                    id: twoImagesTextureDetails.left_img?.id,
                    desktop: createAttachmentType({
                        id: twoImagesTextureDetails.left_img?.desktop.id,
                        label: "Desktop",
                        filename: twoImagesTextureDetails.left_img?.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: twoImagesTextureDetails.left_img?.mobile.id,
                        label: "Mobile",
                        filename: twoImagesTextureDetails.left_img?.mobile.filename,
                        status: 2,
                    }),
                };
                newForm.right_img = {
                    id: twoImagesTextureDetails.right_img?.id,
                    desktop: createAttachmentType({
                        id: twoImagesTextureDetails.right_img?.desktop.id,
                        label: "Desktop",
                        filename: twoImagesTextureDetails.right_img?.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: twoImagesTextureDetails.right_img?.mobile.id,
                        label: "Mobile",
                        filename: twoImagesTextureDetails.right_img?.mobile.filename,
                        status: 2,
                    }),
                };
                newForm.center_left_img = {
                    id: twoImagesTextureDetails.center_left_img?.id,
                    desktop: createAttachmentType({
                        id: twoImagesTextureDetails.center_left_img?.desktop.id,
                        label: "Desktop",
                        filename: twoImagesTextureDetails.center_left_img?.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: twoImagesTextureDetails.center_left_img?.mobile.id,
                        label: "Mobile",
                        filename: twoImagesTextureDetails.center_left_img?.mobile.filename,
                        status: 2,
                    }),
                };
                newForm.center_right_img = {
                    id: twoImagesTextureDetails.center_right_img?.id,
                    desktop: createAttachmentType({
                        id: twoImagesTextureDetails.center_right_img?.desktop.id,
                        label: "Desktop",
                        filename: twoImagesTextureDetails.center_right_img?.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: twoImagesTextureDetails.center_right_img?.mobile.id,
                        label: "Mobile",
                        filename: twoImagesTextureDetails.center_right_img?.mobile.filename,
                        status: 2,
                    }),
                };

                return newForm;
            });
        }
    }, [setForm, twoImagesTextureDetails]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            if (isValid) {
                const promises: Promise<unknown>[] = [];

                const twoImagesTextureBody: SectionFourImagesVerticalBody = {
                    id: blockGroupCompositionId,
                    left_img: {
                        id: form.left_img.id,
                        desktop: converterAttachmentToFileWithPayload(form.left_img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.left_img.mobile),
                    },
                    right_img: {
                        id: form.right_img.id,
                        desktop: converterAttachmentToFileWithPayload(form.right_img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.right_img.mobile),
                    },
                    center_left_img: {
                        id: form.center_left_img.id,
                        desktop: converterAttachmentToFileWithPayload(form.center_left_img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.center_left_img.mobile),
                    },
                    center_right_img: {
                        id: form.center_right_img.id,
                        desktop: converterAttachmentToFileWithPayload(
                            form.center_right_img.desktop
                        ),
                        mobile: converterAttachmentToFileWithPayload(form.center_right_img.mobile),
                    },
                };

                const [apiCall] = saveSectionFourImagesVertical(twoImagesTextureBody);

                promises.push(apiCall);

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        saveSectionFourImagesVertical,
        addSaveAction,
        showAllError,
        blockGroupCompositionId,
        graphicalOrder,
        blockGroupId,
        isValid,
        blockId,
        form,
    ]);

    return React.useMemo(
        (): SectionFourImagesVerticalType => ({
            buttonDelete: deleteSectionFourImagesVertical,
            setCenterLeftImage,
            setCenterRightImage,
            setRightImage,
            setLeftImage,
            formHook,
        }),
        [
            deleteSectionFourImagesVertical,
            setCenterLeftImage,
            setCenterRightImage,
            setRightImage,
            setLeftImage,
            formHook,
        ]
    );
}

export default useSectionFourImagesVertical;
